import { useSelector } from 'react-redux'

import type { EligibleDiscount } from '@library/api-promotions'

import { useDeliveryDateDiscount } from 'hooks/promotions/useDeliveryDateDiscount'
import { formatPrice } from 'utils/format'

import { getNextBoxPreferredDeliveryDate } from '../../selectors/orderDetails'

export type GoustoAnytimeBannerDiscount = {
  // Whether an eligible discount should be surfaced on the banner.
  hasDiscount: boolean
  // Display label for the discount ('20%' or '£15.00'), or null when there's
  // nothing usable to show. Null while eligibility is still resolving too, so
  // the banner holds its standard copy rather than flicker a placeholder.
  discountLabel: string | null
}

// Percentages round up to stay consistent with the order-confirmation savings
// banner; fixed amounts use the standard price format. Amounts that aren't a
// positive number are treated as no discount.
const formatDiscountLabel = (discount: EligibleDiscount | null): string | null => {
  if (!discount) {
    return null
  }

  const amount = Number(discount.amount)
  if (!Number.isFinite(amount) || amount <= 0) {
    return null
  }

  switch (discount.type) {
    case 'percentageOffOrder':
      return `${Math.ceil(amount)}%`
    case 'amountOffOrder':
      return formatPrice(discount.amount)
    default:
      return null
  }
}

/**
 * Discount to surface on the Order Confirmation next-box banner. Looks up the
 * promotions eligibility service for the shared preferred delivery date (the
 * same date the banner's tap target opens on), so the discount shown always
 * matches the order the banner opens.
 */
export const useGoustoAnytimeBannerDiscount = (): GoustoAnytimeBannerDiscount => {
  const preferredDeliveryDate = useSelector(getNextBoxPreferredDeliveryDate)

  const { discount } = useDeliveryDateDiscount({
    enabled: true,
    boxType: 'transactional',
    deliveryDate: preferredDeliveryDate,
  })

  const discountLabel = formatDiscountLabel(discount)

  return { hasDiscount: discountLabel !== null, discountLabel }
}
